/**
 * 69.5f, the commander's pull to the front (GameConfig.frontPull, on unless false; the owner: "bots 'scouting' way deep in
 * friendly territory, and idle bots just hanging around in friendly territory when there's a battle going on one sector over
 * in foothold"). Lane C's diagnosis: the scouts' targets were always at the front, but a scouting squad spent 56 % of its
 * scouting behind its own front, a median 2 sectors deep — the walk up from where it landed, under a scout's label; and a
 * squad the human ordered somewhere stayed there, the commander leaving it be (68.23), after the front moved on.
 *
 * - (a) the scout follows its squad's place: 69.5a's take-made-scout is issued only to a squad within a sector of its circle;
 *   one further back keeps the take — it walks toward the circle's centre as its side knows it (69.2a's sitesAsKnown) — and
 *   is made the scout once near (`scoutReady`);
 * - (b) a squad the plan left with nothing (`free`) is sent to the nearest objective in play its side knows being fought over
 *   (`pullUnordered`) — never one with a human's order: that is flagged on the table instead (core/frontLine.ts leftBehind,
 *   the owner's ruling).
 */
import type { CapturePoint, GameState, SquadOrder, Vec3 } from '../types';
import { sectorAt } from '../core/capturePoints';
import { frontSpan } from '../core/frontLine';
import { arcDistance } from '../core/gameState';

/** (a): may a squad standing at `squadDir` be sent to scout the circle at `circleDir`? (Off, or no front: always.) */
export function scoutReady(state: Pick<GameState, 'modeState' | 'config'>, squadDir: Vec3 | null, circleDir: Vec3): boolean {
  const ps = state.modeState.points;
  if (state.config.frontPull === false || !frontSpan(state) || !ps?.front || !squadDir) return true;
  return Math.abs(sectorAt(ps.front, squadDir) - sectorAt(ps.front, circleDir)) <= 1;
}

/** (b): the squads the plan left `free`, each sent to take the nearest objective in play being fought over (as its side knows the points). */
export function pullUnordered(state: Pick<GameState, 'config' | 'planets'>, orders: Map<number, SquadOrder>, live: ReadonlyArray<{ squad: { id: number }; dir: Vec3 }>, sites: readonly CapturePoint[]): void {
  if (state.config.frontPull === false) return;
  const fought = sites.filter(s => s.capturer !== null);
  if (!fought.length) return;
  for (const x of live) {
    if (orders.get(x.squad.id)?.kind !== 'free') continue;
    const R = state.planets[fought[0].planetIndex].radius;
    const site = fought.reduce((a, s) => (arcDistance(s.surfaceDir, x.dir, R) < arcDistance(a.surfaceDir, x.dir, R) ? s : a));
    orders.set(x.squad.id, { kind: 'take', pointId: site.id });
  }
}
